import selectFolderWithFallback from 'owa-mail-routing/lib/selectFolderWithFallback';
import type { AttachmentFileAttributes } from 'owa-attachment-file-types';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import shouldAutoAddSignature from 'owa-mail-compose-actions/lib/utils/shouldAutoAddSignature';
import newMessage from 'owa-mail-message-actions/lib/actions/newMessage';
import { lazyLoadSignatureInUserOptions } from 'owa-options-message-options';
import { getQueryStringParameters } from 'owa-querystring';
import type Message from 'owa-service/lib/contract/Message';
import processMailToLink, {
    processSubject,
    processBody,
} from 'owa-content-handler/lib/utils/processMailToLink';
import getDefaultBodyType from 'owa-mail-compose-actions/lib/utils/getDefaultBodyType';
import {
    lazyLoadDraftToCompose,
    lazyOpenCompose,
    lazyReplyToMessageAndCreateViewState,
    lazyExtractComposePopoutData,
} from 'owa-mail-compose-actions';
import { getDataFromParentWindow, setPopoutChildState, PopoutChildState } from 'owa-popout-v2';
import { convertRestIdToEwsId } from 'owa-identifiers';
import {
    ComposeOperation,
    ComposeTarget,
    ComposePopoutData,
    ComposeViewStateInitProps,
} from 'owa-mail-compose-store';
import '../orchestrators/onCloseDeeplinkComposeOrchestrator';
import { logUsage } from 'owa-analytics';
import { loadItemReadingPaneForDeeplink } from '../actions/loadItemReadingPaneForDeeplink';

const MAILTO_PREFIX = 'mailto:';

export interface MailComposeRouteParameters {
    folderId?: string;
    draftId?: string;
}

interface ReplyMessage extends Message {
    replyBody: string;
    attachmentFilesToUpload: AttachmentFileAttributes[];
    groupId?: string;
}

export async function deepLinkComposeRouteHandler(parameters: MailComposeRouteParameters) {
    // Add a min width reset.
    window.document.body.style.minWidth = '0';
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2322 (51,9): Type 'null' is not assignable to type 'ComposeViewStateInitProps'.
    // @ts-expect-error
    let composeInitProps: ComposeViewStateInitProps = null;
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2322 (55,9): Type 'null' is not assignable to type 'ComposePopoutData'.
    // @ts-expect-error
    let composePopoutData: ComposePopoutData = null;
    let { folderId, draftId } = parameters;

    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2322 (61,5): Type 'string | null' is not assignable to type 'string | undefined'.
    // @ts-expect-error
    folderId = convertRestIdToEwsId(folderId);
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2322 (65,5): Type 'string | null' is not assignable to type 'string | undefined'.
    // @ts-expect-error
    draftId = convertRestIdToEwsId(draftId);

    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2345 (70,30): Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    // @ts-expect-error
    selectFolderWithFallback(folderId);
    const openCompose = await lazyOpenCompose.import();

    if (draftId) {
        await lazyLoadDraftToCompose.importAndExecute(
            draftId,
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2345 (79,13): Argument of type 'null' is not assignable to parameter of type 'string'.
            // @ts-expect-error
            null, // sxsId
            ComposeTarget.PrimaryReadingPane
        );
    } else if (!!(composeInitProps = getComposeInitPropsFromUrlParameter())) {
        openCompose(composeInitProps, ComposeTarget.PrimaryReadingPane);
    } else if (
        !!(composePopoutData = await getDataFromParentWindow<ComposePopoutData>()) &&
        composePopoutData.composeViewState
    ) {
        const extractComposePopoutData = await lazyExtractComposePopoutData.import();
        const viewState = extractComposePopoutData(composePopoutData);
        openCompose(viewState, ComposeTarget.PrimaryReadingPane);
    } else {
        await newMessage('Route');
    }

    setPopoutChildState(PopoutChildState.Ready);
}

// Handle deeplink compose load draft: /mail/deeplink/compose/draft
// Draft Id will be passed from parent window
export let deepLinkComposeDraftRouteHandler = async () => {
    const popoutData = await getDataFromParentWindow<MailComposeRouteParameters>();
    if (popoutData?.draftId) {
        deepLinkComposeRouteHandler(popoutData);
    } else {
        // Nothing to load, just show a blank page.
        setPopoutChildState(PopoutChildState.Ready);
    }
};

export function deepLinkReplyRouteHandler(parameters: MailComposeRouteParameters) {
    deepLinkReply(false /*isReplyAll*/, parameters);
}

export function deepLinkReplyAllRouteHandler(parameters: MailComposeRouteParameters) {
    deepLinkReply(true /*isReplyAll*/, parameters);
}

async function deepLinkReply(isReplyAll: boolean, parameters: MailComposeRouteParameters) {
    const { draftId } = parameters;
    const item = await getDataFromParentWindow<ReplyMessage>();
    if (!!item || !!draftId) {
        if (
            shouldAutoAddSignature(
                isReplyAll ? ComposeOperation.ReplyAll : ComposeOperation.Reply
            ) &&
            !isSignatureLoaded()
        ) {
            await lazyLoadSignatureInUserOptions.importAndExecute();
        }

        if (draftId) {
            // Always load the item first.
            loadItemReadingPaneForDeeplink(draftId, undefined, undefined, async () => {
                const replyToMessageAndCreateViewState = await lazyReplyToMessageAndCreateViewState.import();

                await replyToMessageAndCreateViewState({
                    isReplyAll: isReplyAll,
                    useFullCompose: true,
                    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                    // -> Error TS2322 (142,21): Type 'null' is not assignable to type 'InstrumentationContext[]'.
                    // @ts-expect-error
                    instrumentationContexts: null,
                    actionSource: 'Link',
                    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                    // -> Error TS2322 (147,21): Type 'null' is not assignable to type 'string | undefined'.
                    // @ts-expect-error
                    conversationId: null,
                    referenceItemOrId: draftId,
                });

                setPopoutChildState(PopoutChildState.Ready);
            });
        } else {
            const replyToMessageAndCreateViewState = await lazyReplyToMessageAndCreateViewState.import();

            await replyToMessageAndCreateViewState({
                isReplyAll: isReplyAll,
                useFullCompose: true,
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2322 (162,17): Type 'null' is not assignable to type 'InstrumentationContext[]'.
                // @ts-expect-error
                instrumentationContexts: null,
                actionSource: 'Link',
                // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
                // -> Error TS2322 (167,17): Type 'null' is not assignable to type 'string | undefined'.
                // @ts-expect-error
                conversationId: null,
                referenceItemOrId: item,
                body: item.replyBody,
                attachmentFiles: item.attachmentFilesToUpload,
                groupId: item.groupId,
            });

            setPopoutChildState(PopoutChildState.Ready);
        }
    } else {
        setPopoutChildState(PopoutChildState.Ready);
    }
}

function getComposeInitPropsFromUrlParameter(): ComposeViewStateInitProps {
    let { mailtouri, to, subject, body } = getQueryStringParameters();

    if (mailtouri) {
        // Respect mailtouri if exists
        let { composeInitProps, isValid, errorMessage } = processMailToLink(mailtouri);
        logUsage('MailtoLinkOpenedWithOWA');
        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2322 (191,9): Type 'false | ComposeViewStateInitProps' is not assignable to type 'ComposeViewStateInitProps'.
        // @ts-expect-error
        return isValid && !errorMessage && composeInitProps;
    } else {
        // Otherwise, see if there is to/subject/body parameters
        let composeInitProps: ComposeViewStateInitProps;
        if (to) {
            to = to.toLowerCase().indexOf(MAILTO_PREFIX) == 0 ? to : MAILTO_PREFIX + to;
            let result = processMailToLink(to);
            if (result.isValid && !result.errorMessage) {
                composeInitProps = result.composeInitProps;
            }
        }

        if (subject) {
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2454 (207,48): Variable 'composeInitProps' is used before being assigned.
            // @ts-expect-error
            composeInitProps = ensureInitProps(composeInitProps);
            composeInitProps.subject = processSubject(subject);
        }

        if (body) {
            // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
            // -> Error TS2454 (215,48): Variable 'composeInitProps' is used before being assigned.
            // @ts-expect-error
            composeInitProps = ensureInitProps(composeInitProps);
            composeInitProps.newContent = processBody(body, getDefaultBodyType());
        }

        // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
        // -> Error TS2454 (222,16): Variable 'composeInitProps' is used before being assigned.
        // @ts-expect-error
        return composeInitProps;
    }
}

function ensureInitProps(props: ComposeViewStateInitProps): ComposeViewStateInitProps {
    return (
        props || {
            operation: ComposeOperation.New,
            bodyType: getDefaultBodyType(),
        }
    );
}

function isSignatureLoaded(): boolean {
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS2339 (239,13): Property 'SignatureHtml' does not exist on type 'RecursiveReadOnly<UserOptionsType | undefined>'.
    // @ts-expect-error
    const { SignatureHtml, SignatureText } = getUserConfiguration().UserOptions;
    return SignatureHtml !== null && SignatureText !== null;
}
